export interface TimelineEvent {
  date: string;
  title: string;
  desc: string;
}

export interface ArticleSection {
  heading: string;
  paragraphs: string[];
}

export interface Article {
  id: string;
  title: string;
  subtitle: string;
  summary: string;
  categoryId: string;
  categoryName: string;
  tags: string[];
  date: string;
  readTime: string;
  heat: number;
  cover: string;
  timeline: TimelineEvent[];
  sections: ArticleSection[];
  takeaways: string[];
}

export const articles: Article[] = [
  {
    id: "talent-show-vote-dispute",
    title: "选秀票数争议复盘：从公示到复核的十四天",
    subtitle: "一次排名倒挂，如何牵出整套投票口径的讨论",
    summary:
      "决赛夜排名公布后，社交平台上出现了大量票数截图对比。我们把从公示、质疑、官方回应到复核结果的全部节点重新排了一遍，看清争议真正卡在哪一环。",
    categoryId: "variety",
    categoryName: "综艺复盘",
    tags: ["选秀", "票数公示", "赛制复核"],
    date: "2026-08-22",
    readTime: "9 分钟",
    heat: 9860,
    cover:
      "https://readdy.ai/api/search-image?query=dark%20moody%20empty%20television%20studio%20with%20rows%20of%20seats%20and%20dim%20warm%20spotlight%2C%20cinematic%20film%20grain%2C%20deep%20charcoal%20and%20amber%20tones%2C%20editorial%20documentary%20photography%2C%20muted%20high%20contrast&width=1200&height=800&seq=arch-variety-vote&orientation=landscape",
    timeline: [
      { date: "决赛夜 22:40", title: "排名公布", desc: "总决赛直播公布最终名次，前三名顺序与此前多家数据站的实时统计出现明显差异。" },
      { date: "第 1 天 01:15", title: "对比图扩散", desc: "有观众将直播画面与第三方统计截图并排发布，相关话题在两小时内进入热搜榜。" },
      { date: "第 2 天", title: "平台首次回应", desc: "节目组发布说明，称统计口径为「有效票」，重复与异常投票会被剔除，未公布具体剔除规则。" },
      { date: "第 5 天", title: "口径说明补充", desc: "在持续追问下，节目组补充说明异常票判定维度，但未提供逐条数据。" },
      { date: "第 9 天", title: "第三方复核介入", desc: "平台称已委托外部机构对计票系统进行复核，复核对象为系统日志而非公开票数。" },
      { date: "第 14 天", title: "复核结论公布", desc: "结论认定「计票过程无系统性问题」，同时确认公示口径与大众理解存在偏差，节目组致歉。" },
    ],
    sections: [
      {
        heading: "争议的起点：两个口径在同时被使用",
        paragraphs: [
          "整件事的核心并不在于谁该第几，而在于「有效票」这个概念从一开始就没有被解释清楚。观众看到的第三方统计通常是原始票，而节目组统计的是剔除异常后的有效票，两个数字天生就对不上。",
          "当两种口径同时出现在公众视野里，却没有任何一方向前说明差异来源时，对比图就会自动被解读成「暗箱操作」。这是十四天里反复出现的同一个逻辑起点。",
        ],
      },
      {
        heading: "回应为什么没能止住质疑",
        paragraphs: [
          "第 2 天的首次回应使用了大量专业表述，但没有给出任何可验证的中间数据。对观众来说，一份「我们统计是对的」的声明，和一个看得见的数字之间，还隔着很远。",
          "直到第 5 天补充规则说明，讨论的方向才从「有没有作假」转向「规则合不合理」。这个转变很关键，它说明观众真正想要的其实是可复核的过程，而不是一句结论。",
        ],
      },
      {
        heading: "复核范围决定了结论的说服力",
        paragraphs: [
          "第 9 天公布的复核对象是系统日志。从技术角度这能证明计票程序没有被篡改，但它无法回答观众最关心的那部分：为什么会有人理解成另一个数字。",
          "换句话说，复核证明了「机器没问题」，却没有回应「沟通有问题」。这也是结论公布后讨论没有立刻平息的原因。",
        ],
      },
      {
        heading: "后续影响：公示口径开始被写进赛制",
        paragraphs: [
          "事件之后，同类节目陆续在开播前就公布了投票统计口径与异常票剔除标准，并在决赛夜同步公开剔除数量。",
          "回头看，这次争议真正的价值，是把一个长期模糊的默认规则，逼成了需要提前说明的公开承诺。",
        ],
      },
    ],
    takeaways: [
      "争议的根源是口径未提前公示，而不是计票本身。",
      "复核范围只覆盖了「机器」，没有覆盖「沟通」。",
      "事后同类节目开始把统计口径写进赛前说明。",
    ],
  },
  {
    id: "film-schedule-flip",
    title: "院线电影紧急改档复盘：官宣到撤档的七十二小时",
    subtitle: "一张排片表的变化，背后是三条同时推进的时间线",
    summary:
      "一部倍受期待的影片在上映前三天突然宣布改档。我们把宣发侧、影院侧和观众侧的动作拆开，重新拼出这七十二小时里到底发生了什么。",
    categoryId: "film",
    categoryName: "影视风波",
    tags: ["改档", "宣发", "排片"],
    date: "2026-08-15",
    readTime: "8 分钟",
    heat: 8720,
    cover:
      "https://readdy.ai/api/search-image?query=dark%20empty%20cinema%20hall%20with%20red%20velvet%20seats%20and%20faint%20projector%20light%2C%20warm%20amber%20glow%2C%20cinematic%20moody%20atmosphere%2C%20film%20grain%2C%20editorial%20photography%2C%20deep%20charcoal%20tones&width=1200&height=800&seq=arch-film-flip&orientation=landscape",
    timeline: [
      { date: "T-72 小时", title: "点映场次追加", desc: "片方宣布在全国追加一批点映场次，购票平台同步开票，多个城市黄金场次迅速售罄。" },
      { date: "T-48 小时", title: "预售节奏放缓", desc: "部分城市预售增速明显低于同期同量级影片，排片经理开始重新评估场次分配。" },
      { date: "T-36 小时", title: "影院收到口头通知", desc: "多家影院反馈收到改档的口头预告，但未获得书面文件，排片表暂未调整。" },
      { date: "T-24 小时", title: "官方公告发布", desc: "片方以「技术原因」发布改档公告，新档期未定，随即启动已售影票的自动退票。" },
      { date: "T-8 小时", title: "退票与解释", desc: "购票平台开放无条件退票，部分点映观众收到补偿券，片方未进一步说明「技术原因」。" },
      { date: "T+0", title: "原档期当日", desc: "原上映日，影院排片表已替换为其他影片，社交平台出现大量关于改档原因的讨论。" },
    ],
    sections: [
      {
        heading: "改档不是单点决定，而是三条线的交汇",
        paragraphs: [
          "把时间拉平看，宣发侧、影院侧和观众侧其实一直在同步推进。点映追加说明片方对口碑有信心，预售放缓说明市场反馈保守，影院的口头通知则说明决定至少提前了一天以上。",
          "很多讨论把它归结为「临时决定」，但从节点密度看，更像是一个观察了至少两天的判断，只是公布得足够晚。",
        ],
      },
      {
        heading: "「技术原因」为什么成为通用说法",
        paragraphs: [
          "在正式公告里，「技术原因」是一个成本最低的表述。它既不需要解释市场判断，也不用承担对合作方的说明义务，同时还给后续留了回旋空间。",
          "问题在于，这个说法用得太多了，以至于观众已经默认它是「不方便说的原因」的代名词。信息真空一旦形成，讨论自然会往猜测方向走。",
        ],
      },
      {
        heading: "退票处理是这次少有的加分项",
        paragraphs: [
          "公告发布后不到半天，主要购票平台就开放了无条件退票，并对已看点映的观众发放补偿券。这套动作明显是预案里就写好的。",
          "对比以往改档后出现的退票滞后问题，这次执行效率明显更高，也说明行业在流程上确实做过总结。",
        ],
      },
      {
        heading: "后续影响：新档期选择更谨慎",
        paragraphs: [
          "改档之后，片方在两个月后才公布新档期，并且刻意避开了同类型影片密集的窗口。",
          "从结果看，这次调整换来了更宽松的排片环境，但代价是前期宣发资源的重复投入。",
        ],
      },
    ],
    takeaways: [
      "改档决定至少提前一天形成，公布时机才是争议点。",
      "「技术原因」的信息真空会直接转化为猜测。",
      "退票与补偿的预案化处理显著降低了负面观感。",
    ],
  },
  {
    id: "variety-editing-storm",
    title: "综艺剪辑争议复盘：镜头分配如何一步步引爆舆论",
    subtitle: "同样的素材，不同的剪法，走向完全不同",
    summary:
      "一档常驻综艺因某一期正片的镜头分配引发大规模讨论。我们按播出前后的时间顺序，梳理了从路透、正片到加更版的完整链条。",
    categoryId: "variety",
    categoryName: "综艺复盘",
    tags: ["综艺剪辑", "镜头分配", "加更版"],
    date: "2026-08-09",
    readTime: "7 分钟",
    heat: 7640,
    cover:
      "https://readdy.ai/api/search-image?query=dark%20editing%20suite%20with%20multiple%20monitors%20showing%20timeline%20software%20and%20warm%20desk%20lamp%2C%20moody%20low%20light%2C%20amber%20and%20charcoal%20tones%2C%20cinematic%20editorial%20photography%2C%20shallow%20depth%20of%20field&width=1200&height=800&seq=arch-variety-edit&orientation=landscape",
    timeline: [
      { date: "录制周", title: "路透流出", desc: "现场观众在社交平台发布录制路透，提到某位嘉宾当期参与环节较多。" },
      { date: "播出前 2 天", title: "预告发布", desc: "官方预告片集中展示了另一位嘉宾的片段，形成第一轮预期。" },
      { date: "播出当晚", title: "正片上线", desc: "正片中预期嘉宾镜头明显偏少，相关话题在播出后两小时内快速升温。" },
      { date: "播出次日", title: "对比剪辑扩散", desc: "观众把预告与正片画面逐帧对比，指出多个被剪掉的互动环节。" },
      { date: "播出第 3 天", title: "加更版上线", desc: "平台上线加更版，补回了部分被讨论的片段，但未对剪辑逻辑作出说明。" },
      { date: "播出第 6 天", title: "节目组回应", desc: "节目组表示剪辑以「当期叙事节奏」为准，否认存在针对性删减。" },
    ],
    sections: [
      {
        heading: "预期是怎么被建立起来的",
        paragraphs: [
          "争议的种子其实在预告片发布时就埋下了。预告集中呈现了某位嘉宾的高光片段，观众自然形成了「这一期是他的主场」的预期。",
          "当正片与预期出现明显落差时，落差本身就会被解读成一种态度。这是很多剪辑争议的共同起点。",
        ],
      },
      {
        heading: "逐帧对比为什么杀伤力这么大",
        paragraphs: [
          "对比剪辑把预告、正片和路透放在同一时间轴上，让「被剪掉」变得可视化。相比主观感受，画面并列的说服力要强得多。",
          "但也要注意，逐帧对比只能证明片段被删，无法直接证明删除的动机。这两者之间需要额外证据才能连接。",
        ],
      },
      {
        heading: "加更版是补偿还是补充说明",
        paragraphs: [
          "第 3 天上线的加更版补回了被讨论的片段，这在操作上是一种回应，但它没有解释为什么会剪掉。",
          "对一部分观众来说，补回内容等于承认问题；对另一部分来说，没有说明动机就仍然不算回应。分歧也由此产生。",
        ],
      },
      {
        heading: "后续影响：剪辑说明开始出现在片尾",
        paragraphs: [
          "此后该节目在正片片尾增加了简短的剪辑说明，交代当期取舍的主要考虑。",
          "行业里也有更多节目开始在争议出现时，第一时间说明素材总量与成片时长的客观限制。",
        ],
      },
    ],
    takeaways: [
      "预告建立的预期落差，往往是争议的第一现场。",
      "逐帧对比能证明删减，但证明不了动机。",
      "片尾剪辑说明逐渐成为同类节目的常规动作。",
    ],
  },
  {
    id: "song-copyright-tug",
    title: "热歌版权归属复盘：一首歌的三次易主",
    subtitle: "词曲、录音与翻唱，三套权利被混在了一起",
    summary:
      "一首翻红老歌在半年内被指涉及三方权利主张。我们把授权链条上的每一次转手按时间排开，看看争议究竟出在哪一层。",
    categoryId: "music",
    categoryName: "音乐版权",
    tags: ["版权", "授权链条", "翻唱"],
    date: "2026-08-03",
    readTime: "10 分钟",
    heat: 6980,
    cover:
      "https://readdy.ai/api/search-image?query=dark%20recording%20studio%20control%20room%20with%20analog%20mixing%20console%20and%20warm%20indicator%20lights%2C%20moody%20low%20key%20lighting%2C%20amber%20and%20deep%20brown%20tones%2C%20cinematic%20editorial%20photography%2C%20film%20grain&width=1200&height=800&seq=arch-music-rights&orientation=landscape",
    timeline: [
      { date: "版本 A 上线", title: "原版发行", desc: "词曲作者与演唱者不同的原版歌曲发行，作品登记信息显示词曲与录音分属不同权利方。" },
      { date: "两年后", title: "首次授权转让", desc: "录音制作者权通过协议转让给一家音乐公司，词曲权利未一并转让。" },
      { date: "第四年", title: "翻红与翻唱", desc: "歌曲因短视频二次走红，多个翻唱版本上线，授权范围集中在录音使用。" },
      { date: "第五年", title: "第一份主张", desc: "词曲方发布声明，称翻唱版本的词曲使用未获得完整授权。" },
      { date: "第五年 次月", title: "第二份主张", desc: "录音权方回应称自身授权链条完整，争议应指向翻唱方而非原录音。" },
      { date: "第五年 第三月", title: "协商结果", desc: "三方达成补充授权协议，翻唱版本补办词曲授权，未进入诉讼程序。" },
    ],
    sections: [
      {
        heading: "三种权利，一开始就被混着谈了",
        paragraphs: [
          "一首歌至少涉及词曲著作权、录音制作者权和表演者权。这三者可以分别转让、分别授权，也经常分属不同的人或公司。",
          "这场争议里，大量讨论把「这首歌归谁」当成一个问题，但实际存在的主张对象至少有三个，混在一起谈就永远谈不拢。",
        ],
      },
      {
        heading: "授权链条断在了哪一环",
        paragraphs: [
          "从节点看，词曲权利自始至终没有随录音权一起转让。翻唱方拿到的授权覆盖的是「使用原录音」，而不是「重新演唱词曲」。",
          "这就是断点所在：翻唱行为天然需要词曲授权，而这条线从未被打通。",
        ],
      },
      {
        heading: "为什么最终没有走诉讼",
        paragraphs: [
          "三方在第二份主张公布后一个月内达成补充协议，翻唱版本补办了词曲授权，历史收益按比例结算。",
          "对各方来说，走完诉讼周期可能超过歌曲的热度窗口，协商是成本最低的选择，这也是音乐版权纠纷最常见的结局。",
        ],
      },
      {
        heading: "后续影响：授权范围开始逐条列明",
        paragraphs: [
          "事件之后，部分音乐平台在与创作者签约时，把「录音使用」与「词曲使用」在合同里分列，并要求翻唱申请单独确认。",
          "条款更细了，沟通成本短期上升，但长期看反而减少了下架和补授权的麻烦。",
        ],
      },
    ],
    takeaways: [
      "词曲权、录音权、表演者权必须分开谈，否则无法定位争点。",
      "翻唱争议多数断在词曲授权这一环。",
      "补充授权与按比例结算，通常比诉讼更符合各方利益。",
    ],
  },
  {
    id: "livestream-refund-wave",
    title: "直播带货退款潮复盘：从爆单到退单的完整链路",
    subtitle: "一场专场直播后，退款率在三天内翻了三倍",
    summary:
      "某场主题直播创造了亮眼的成交数据，但随后出现了集中退款。我们按时间顺序还原了从选品、讲解、售后到平台介入的全过程。",
    categoryId: "live",
    categoryName: "直播电商",
    tags: ["直播带货", "退款", "售后链路"],
    date: "2026-07-28",
    readTime: "8 分钟",
    heat: 9210,
    cover:
      "https://readdy.ai/api/search-image?query=dark%20backstage%20of%20a%20live%20commerce%20studio%20with%20ring%20lights%20and%20stacked%20parcels%2C%20warm%20amber%20and%20charcoal%20tones%2C%20moody%20cinematic%20lighting%2C%20editorial%20documentary%20photography%2C%20film%20grain&width=1200&height=800&seq=arch-live-refund&orientation=landscape",
    timeline: [
      { date: "直播前 5 天", title: "选品与预热", desc: "专场公布主推商品清单，预热短视频集中强调限时价格与赠品组合。" },
      { date: "直播当晚", title: "集中下单", desc: "直播四小时内主推品多次补货，成交额创该账号专场新高。" },
      { date: "发货第 1 天", title: "首批反馈", desc: "已收到货的买家开始反馈赠品与页面描述不符，相关评价陆续出现。" },
      { date: "发货第 2 天", title: "退款申请上升", desc: "退款申请数量明显上升，主要集中在赠品缺失与尺码不符两类原因。" },
      { date: "发货第 3 天", title: "客服通道拥堵", desc: "商家客服响应变慢，部分用户转向平台投诉通道，退款率较日常翻了三倍。" },
      { date: "第 7 天", title: "平台介入与方案", desc: "平台公布处理方案：无理由退货运费由商家承担，赠品按缺件补发或折价退款。" },
    ],
    sections: [
      {
        heading: "爆单与退单之间，只隔着一个赠品描述",
        paragraphs: [
          "这次退款潮的直接触发点出乎意料地具体：页面上的赠品组合在发货时没有完整兑现。金额不大，但因为它出现在用户拿到货的第一时间，所以影响面很广。",
          "在直播电商里，赠品常常被当成提高转化的工具，但它同时也是履约成本，一旦没有提前锁定库存，问题就会在发货阶段集中爆发。",
        ],
      },
      {
        heading: "为什么客服越忙，退款越多",
        paragraphs: [
          "第 3 天出现了典型的负反馈循环：退款申请增加导致客服响应变慢，响应变慢让用户转向平台投诉，投诉又进一步挤占了商家的人力。",
          "这说明售后的处理能力并没有随直播成交规模同步扩容，属于典型的后端跟不上的问题。",
        ],
      },
      {
        heading: "平台方案的关键在运费归属",
        paragraphs: [
          "平台最终把无理由退货的运费划归商家承担，这一步直接改变了用户的决策成本，也让退款处理速度回升。",
          "对商家来说，这是一次明确的成本信号：承诺赠品和尺码表，最终都要计入履约预算。",
        ],
      },
      {
        heading: "后续影响：选品清单增加了库存校验",
        paragraphs: [
          "此后该账号在专场预热阶段增加了赠品库存校验环节，并在商品页标注「赠品以实际到货为准」的替代方案。",
          "行业层面，多家机构开始把退货率纳入专播复盘的核心指标，而不再只看成交额。",
        ],
      },
    ],
    takeaways: [
      "赠品履约是最容易被忽视、也最容易引发集中退款的环节。",
      "售后处理能力必须与成交规模同步扩容。",
      "运费归属一旦明确，退款处理效率会明显回升。",
    ],
  },
  {
    id: "artist-contract-exit",
    title: "艺人解约风波复盘：时间线与关键节点的完整梳理",
    subtitle: "一份声明、两份回应与半年沉默",
    summary:
      "某艺人与其工作室的解约争议持续了半年，公开信息零散且互相矛盾。我们把这些片段按时间归位，理清双方主张各自的依据。",
    categoryId: "contract",
    categoryName: "合约纠纷",
    tags: ["解约", "经纪合约", "分成"],
    date: "2026-07-20",
    readTime: "11 分钟",
    heat: 8120,
    cover:
      "https://readdy.ai/api/search-image?query=dark%20wooden%20desk%20with%20signed%20paper%20documents%20and%20a%20pen%20under%20a%20warm%20desk%20lamp%2C%20moody%20chiaroscuro%20lighting%2C%20amber%20and%20deep%20brown%20tones%2C%20cinematic%20editorial%20photography&width=1200&height=800&seq=arch-contract-exit&orientation=landscape",
    timeline: [
      { date: "争议前 8 个月", title: "合约到期前协商", desc: "原合约临近到期，双方就续约条件进行协商，公开信息显示分歧集中在分成比例。" },
      { date: "争议前 3 个月", title: "工作安排停摆", desc: "艺人方公开行程明显减少，部分已官宣的品牌活动被替换。" },
      { date: "第 1 天", title: "艺人方声明", desc: "艺人方发布声明，主张原合约部分条款履行不到位，提出解约。" },
      { date: "第 3 天", title: "工作室回应", desc: "工作室回应称合约仍在有效期内，单方解约不成立，并公布部分履约记录。" },
      { date: "第 10 天", title: "双方各出证据", desc: "双方先后公布沟通记录与结算明细，但两份材料的口径在时间归属上无法对应。" },
      { date: "第 6 个月", title: "和解公告", desc: "双方发布简短联合公告，宣布达成和解，未披露具体条款。" },
    ],
    sections: [
      {
        heading: "分歧的实质是分成，不是要不要走",
        paragraphs: [
          "从时间线能看出，争议的起点并不是「想不想续约」，而是续约条件里的分成比例没谈拢。工作安排停摆出现在协商之后，说明双方当时已经在做最坏打算。",
          "把这一点理清后，后面所有的声明都能找到解释：双方都在为谈判争取更有利的位置。",
        ],
      },
      {
        heading: "两份材料为什么对不上",
        paragraphs: [
          "第 10 天公布的沟通记录与结算明细里，同一个工作节点的归属时间不一致。这未必是某一方造假，也可能是双方采用的结算周期不同。",
          "但公开层面没有解释这个差异，导致讨论迅速从「条款争议」滑向「谁在说谎」，反而模糊了真正的分歧。",
        ],
      },
      {
        heading: "半年沉默期的意义",
        paragraphs: [
          "从第 10 天到第 6 个月，双方几乎停止公开表态。这段时间通常用于谈判与取证，公开沉默本身就是一种谈判策略。",
          "对旁观者来说，沉默期容易被误读成「理亏」，但从业内惯例看，它更接近争议进入实质性协商的信号。",
        ],
      },
      {
        heading: "后续影响：新合约普遍细化结算条款",
        paragraphs: [
          "和解之后，行业里关于经纪合约结算周期的讨论明显增多，部分新签合约开始明确约定月度对账与争议处理时限。",
          "这或许是这场风波留下的最实际的东西：把模糊地带写清楚。",
        ],
      },
    ],
    takeaways: [
      "停摆行程发生在协商之后，说明分歧早已实质化。",
      "结算周期不同会造成材料「对不上」的观感。",
      "公开沉默期通常是谈判进入实质阶段的标志。",
    ],
  },
  {
    id: "idol-debut-lineup",
    title: "出道名单临门生变复盘：一次深夜公告的连锁反应",
    subtitle: "公告发出的两个小时内，发生了什么",
    summary:
      "一份深夜发布的出道名单调整公告，让原本已经预告的阵容发生变化。我们把公告前后的关键动作按小时拆开，还原信息是如何一层层扩散的。",
    categoryId: "timeline",
    categoryName: "舆论时间线",
    tags: ["出道名单", "深夜公告", "舆情扩散"],
    date: "2026-07-12",
    readTime: "7 分钟",
    heat: 7350,
    cover:
      "https://readdy.ai/api/search-image?query=dark%20empty%20concert%20stage%20before%20showtime%20with%20a%20single%20warm%20spotlight%20and%20hazy%20atmosphere%2C%20cinematic%20moody%20lighting%2C%20deep%20charcoal%20and%20amber%20tones%2C%20editorial%20photography%2C%20film%20grain&width=1200&height=800&seq=arch-idol-lineup&orientation=landscape",
    timeline: [
      { date: "22:47", title: "预告发出", desc: "官方账号发布次日发布会预告，文案中出现完整的阵容名单。" },
      { date: "23:20", title: "名单调整", desc: "原预告被删除，替换为无名单版本的预告，未作任何说明。" },
      { date: "23:55", title: "深夜公告", desc: "官方以简讯形式发布调整公告，说明出道名单发生变动，发布会延期。" },
      { date: "次日 00:40", title: "讨论集中爆发", desc: "被替换成员的相关话题进入热搜前列，粉丝发布大量对比截图。" },
      { date: "次日 09:00", title: "相关方表态", desc: "被替换成员所在机构发布简短声明，表示「尊重最终安排」，未透露细节。" },
      { date: "第 5 天", title: "补位与发布会", desc: "延期后的发布会举行，补位成员亮相，官方仍未说明调整原因。" },
    ],
    sections: [
      {
        heading: "删除预告这一动作，放大了信息量",
        paragraphs: [
          "22:47 的预告里名单是完整的，33 分钟后被替换成无名单版本。这个「删除再替换」的动作本身，比任何文字都更直接地传递了「名单确实变了」。",
          "如果一开始就发出无名单版本，讨论的热度可能完全不同。信息操作上的犹豫，反而成了最强的导火索。",
        ],
      },
      {
        heading: "深夜时间点的双刃剑",
        paragraphs: [
          "23:55 的公告选在深夜发布，通常是想降低即时关注度。但对粉丝群体来说，深夜恰恰是活跃度最高的时段。",
          "结果公告发出不到一小时，讨论就完成了从核心粉丝到路人的扩散，深夜策略在这个场景下基本失效。",
        ],
      },
      {
        heading: "「尊重最终安排」意味着什么",
        paragraphs: [
          "被替换成员机构的表态只有一句话，既没有指责主办方，也没有确认成员的去向。这种表述在行业内通常意味着后续还有协商空间。",
          "它同时也说明，双方在公告发布前就已经沟通过，而不是单方面被通知。",
        ],
      },
      {
        heading: "后续影响：名单确认流程被前置",
        paragraphs: [
          "此后同类项目普遍把最终名单确认放到预告发布之前完成，并设置至少 48 小时的确认静默期。",
          "这次事件的价值，在于把「先官宣后调整」的高风险流程彻底暴露了出来。",
        ],
      },
    ],
    takeaways: [
      "删除并替换预告，本身就是高强度的信息表达。",
      "深夜发布对高活跃粉丝群体反而起不到降温作用。",
      "名单确认流程被行业普遍前置并设置静默期。",
    ],
  },
  {
    id: "boxoffice-data-rumor",
    title: "票房数据口径传闻复盘：同一部电影的三种解释",
    subtitle: "数字没变，解读变了三次",
    summary:
      "关于某部影片票房数据的讨论从「注水」到「误读」再到「口径差异」，前后持续了近一个月。我们把三种解释的出现顺序整理清楚。",
    categoryId: "film",
    categoryName: "影视风波",
    tags: ["票房", "数据口径", "行业讨论"],
    date: "2026-07-05",
    readTime: "9 分钟",
    heat: 6420,
    cover:
      "https://readdy.ai/api/search-image?query=dark%20statistics%20room%20with%20glowing%20charts%20on%20screens%20and%20warm%20amber%20backlight%2C%20moody%20low%20light%2C%20deep%20charcoal%20tones%2C%20cinematic%20editorial%20photography%2C%20film%20grain&width=1200&height=800&seq=arch-boxoffice-data&orientation=landscape",
    timeline: [
      { date: "首周末", title: "数据公布", desc: "影片首周末票房公布，单日曲线在第三天出现反常的逆跌回升。" },
      { date: "第 4 天", title: "注水质疑", desc: "有分析指出深夜场次上座率异常偏高，相关讨论开始扩散。" },
      { date: "第 8 天", title: "误读说法出现", desc: "有从业者发文称异常数据来自统计时段的划分方式，属于误读。" },
      { date: "第 15 天", title: "口径差异被提出", desc: "第三种解释出现：不同数据源统计的场次范围不同，导致单日数值无法直接对比。" },
      { date: "第 21 天", title: "行业协会说明", desc: "行业协会发布说明，公布统一统计口径的建议，未对该片作出定性。" },
      { date: "第 30 天", title: "讨论自然回落", desc: "随着影片下映，讨论量下降，三种解释均未被官方结论采纳或否认。" },
    ],
    sections: [
      {
        heading: "同一个数字，为什么能有三套解释",
        paragraphs: [
          "最早的质疑基于单日曲线的形态，这是一种直觉判断；随后的「误读」说法基于统计时段，属于方法层面；最后的「口径差异」则指向数据源本身。",
          "三者并不完全互相排斥，它们分别解释了不同层面的问题，但被放进同一场讨论后，就显得像是在互相打架。",
        ],
      },
      {
        heading: "缺少统一口径，讨论就无法收敛",
        paragraphs: [
          "整个月里，没有任何一方公布过完整的原始场次数据。这导致每种解释都无法被验证，讨论只能靠信源可信度来站队。",
          "协会第 21 天的说明虽然提出了统一口径的建议，但来得太晚，舆论的注意力已经转移。",
        ],
      },
      {
        heading: "为什么最终没有定性",
        paragraphs: [
          "从节点看，三种解释都存在合理之处，也都有无法证实的部分。在这种情况下，不下结论反而是负责任的做法。",
          "对读者来说，更值得记住的或许是：当数据口径没有公开时，任何一方的断言都要打个折扣。",
        ],
      },
      {
        heading: "后续影响：数据说明附注变多",
        paragraphs: [
          "此后多部影片在公布票房时，开始在说明里附上场次统计范围与更新时点。",
          "一个月的争论，最终换来了几个更清楚的附注，这大概是它最实在的产出。",
        ],
      },
    ],
    takeaways: [
      "三种解释分别指向直觉、方法和数据源三个层面。",
      "缺少原始数据公开，讨论只能靠信源站队。",
      "票房说明开始附上统计范围与更新时点。",
    ],
  },
];

export const getArticleById = (id: string): Article | undefined =>
  articles.find((item) => item.id === id);